import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Typography, Paper, TextField, Grid, Button, MenuItem,
  FormHelperText, Box, Container, Card, CardContent,
  Checkbox, FormControlLabel, Alert,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { Link as MuiLink } from '@mui/material';
import axios from '../utils/api';
import { US_STATES } from '../utils/constants';

interface ValidationErrors {
  clientCity?: string;
  clientState?: string;
  clientZipCode?: string;
  clientLegalIssue?: string;
  disclaimerAgreed?: string;
}

const ReferralRequestForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get('email') || '';
  const token = searchParams.get('token') || '';

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [disclaimerAgreed, setDisclaimerAgreed] = useState(false);

  const [form, setForm] = useState({
    clientIdentifier: '',
    clientCity: '',
    clientState: '',
    clientZipCode: '',
    clientLegalIssue: '',
  });
 

  const dashboardUrl = `/register/dashboard?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`;

  // const validatePhoneNumber = (phone: string) => {
  //   const cleaned = phone.replace(/\D/g, '');
  //   const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  //   return phoneRegex.test(cleaned);
  // };

  // const formatPhoneNumber = (phoneNumber: string): string => {
  //   const cleaned = phoneNumber.replace(/\D/g, '');
  //   if (cleaned.length === 10) {
  //     return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  //   }
  //   if (cleaned.length === 11 && cleaned.startsWith('1')) {
  //     return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  //   }
  //   return phoneNumber;
  // };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (validationErrors[e.target.name as keyof ValidationErrors]) {
      setValidationErrors({ ...validationErrors, [e.target.name]: undefined });
    }
  };

  // const handlePhoneBlur = () => {
  //   if (form.clientPhone.trim()) {
  //     const formatted = formatPhoneNumber(form.clientPhone);
  //     setForm(prev => ({ ...prev, clientPhone: formatted }));
  //   }
  // };

  const validate = (): boolean => {
    const errors: ValidationErrors = {};

    // if (!form.clientFirstName.trim()) errors.clientFirstName = 'Client first name is required';
    // if (!form.clientLastName.trim()) errors.clientLastName = 'Client last name is required';
    // if (!form.clientPhone.trim()) {
    //   errors.clientPhone = 'Client phone number is required';
    // } else if (!validatePhoneNumber(form.clientPhone)) {
    //   errors.clientPhone = 'Please enter a valid phone number';
    // }
    // if (!form.clientAddressLine1.trim()) errors.clientAddressLine1 = 'Client street address is required';
    if (!form.clientCity.trim()) errors.clientCity = 'Client city is required';
    if (!form.clientState.trim()) errors.clientState = 'Client state is required';
    // if (!form.clientZipCode.trim()) {
    //   errors.clientZipCode = 'Zip code is required';
    // } else if (!/^\d{5}$/.test(form.clientZipCode)) {
    //   errors.clientZipCode = 'Please enter a valid 5-digit zip code';
    // }
    if (form.clientZipCode.trim() && !/^\d{5}$/.test(form.clientZipCode)) { 
      errors.clientZipCode = 'Please enter a valid 5-digit zip code';
    }
    if (!form.clientLegalIssue.trim()) errors.clientLegalIssue = "Please describe the client's legal issue";
    if (!disclaimerAgreed) errors.disclaimerAgreed = 'You must agree to the terms to submit';

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setSubmitted(false);
    setSubmitting(false);
    setSubmitError(null);
    setValidationErrors({});
    setDisclaimerAgreed(false);
    setForm({
      clientIdentifier: '',
      clientCity: '',
      clientState: '',
      clientZipCode: '',
      clientLegalIssue: '',
    });
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      await axios.post('/api/referral-requests', {
        email,
        token,
        clientIdentifier: form.clientIdentifier,
        clientCity: form.clientCity,
        clientState: form.clientState,
        clientZipCode: form.clientZipCode,
        clientLegalIssue: form.clientLegalIssue,
        disclaimerAgreed: true,
      });
      setSubmitted(true);
    } catch (err: any) {
      setSubmitError(err?.response?.data?.error || err?.response?.data?.message || 'Failed to submit referral request.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Container maxWidth="md">
        <Card sx={{ mt: 4 }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
            <Typography variant="h4" gutterBottom color="success.main">
              Lawyer Search Request Submitted
            </Typography>
            <Typography variant="body1" sx={{ mb: 4 }}>
              Your search request has been submitted successfully. An ATJustice representative will be in touch.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(dashboardUrl)}
              sx={{ mr: 2 }}
            >
              Back to Dashboard
            </Button>
            <Button
              variant="contained"
              onClick={resetForm}
            >
              Submit Another Search Request
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: { xs: 2, sm: 4 }, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Lawyer Search Request
          </Typography>
          <Button variant="text" onClick={() => navigate(dashboardUrl)}>
            Back to Dashboard
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Complete the form below to submit a request to find lawyer for your client. All fields marked with * are required.
        </Typography>

        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {submitError}
          </Alert>
        )}

        <form onSubmit={e => e.preventDefault()}>
          <Grid container spacing={2}>
            <Grid item xs={12} >
              <TextField
                label="Client Identifier"
                name="clientIdentifier"
                value={form.clientIdentifier}
                onChange={handleChange}
                fullWidth
                helperText="Please fill in the client's name or another identifier for your reference"
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                label="Client City"
                name="clientCity"
                value={form.clientCity}
                onChange={handleChange}
                fullWidth
                required
                error={!!validationErrors.clientCity}
                helperText={validationErrors.clientCity}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Client State"
                name="clientState"
                value={form.clientState}
                onChange={handleChange}
                fullWidth
                required
                error={!!validationErrors.clientState}
                helperText={validationErrors.clientState}
              >
                {US_STATES.map(state => (
                  <MenuItem key={state} value={state}>{state}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Client Zip Code"
                name="clientZipCode"
                value={form.clientZipCode}
                onChange={handleChange}
                fullWidth
                inputProps={{ maxLength: 5 }}
                error={!!validationErrors.clientZipCode}
                helperText={validationErrors.clientZipCode}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Please briefly describe the matter where the client is seeking legal assistance."
                name="clientLegalIssue"
                value={form.clientLegalIssue}
                onChange={handleChange}
                fullWidth
                required
                multiline
                minRows={5}
                error={!!validationErrors.clientLegalIssue}
                helperText={validationErrors.clientLegalIssue}
              />
            </Grid>

            {/* Disclaimer box */}
            <Grid item xs={12}>
              <Box sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                bgcolor: 'grey.50',
                mt: 1,
              }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Please include in your description above:
                </Typography>
                <Box component="ul" sx={{ mt: 0, pl: 3 }}>
                  <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                    Any pertinent dates, such as date of contract, accident or injury
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                    If there is a monetary claim, please indicate the amount, or range of amounts at issue
                  </Typography>
                  <Typography component="li" variant="body2">
                    If any other locations are involved, beyond the client city and client state. For example, if the client was involved in an accident or injury in another state or county, a divorce where the client's spouse and/or children reside elsewhere, or a real estate transaction in a different location
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={disclaimerAgreed}
                    onChange={e => {
                      setDisclaimerAgreed(e.target.checked);
                      if (validationErrors.disclaimerAgreed) {
                        setValidationErrors({ ...validationErrors, disclaimerAgreed: undefined });
                      }
                    }}
                  />
                }
                label={
                  <Typography variant="body2">
                    I confirm that I have read and agreed to the ATJustice{' '}
                    <MuiLink
                      href="https://www.atjustice.org/terms-and-conditions"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                    >
                      Website Terms &amp; Conditions
                    </MuiLink>
                    <Typography component="span" sx={{ color: 'error.main' }}> *</Typography>
                  </Typography>
                }
                sx={{ mt: 1, alignItems: 'center' }}
              />
              {validationErrors.disclaimerAgreed && (
                <FormHelperText error>{validationErrors.disclaimerAgreed}</FormHelperText>
              )}
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="outlined" onClick={() => navigate(dashboardUrl)}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting}
                  size="large"
                >
                  {submitting ? 'Submitting...' : 'Submit Referral Request'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default ReferralRequestForm;
